/**
 * Hubs marché SEO (pays ≠ langue).
 * Langue marketing globale = fr|en|es|de|it (localeRoutes) — pas de locale langue `/nl` globale.
 * Marché = /be, /nl, /ch… — contenu géo + CTA signup pays, sans hreflang régional.
 * Variante Flandre : /be/nl + /be/nl/{slug} (contenu nl sous le marché BE).
 * Pays-Bas : /nl + /nl/{slug} (contenu nl au root du hub ; distinct de /be/nl).
 *
 * @see docs/seo-international-pro/08-MARKET-HUBS-BE.md
 */

import type { SupportedCountryCode } from './countryConfig';

export type MarketingMarketCode = 'be' | 'nl';

/** Langue de contenu sous un hub marché. */
export const MARKET_CONTENT_LANGS = ['fr', 'nl'] as const;
export type MarketContentLang = (typeof MARKET_CONTENT_LANGS)[number];

export type MarketMetierSlug = 'educateur-canin' | 'pet-sitter';

export interface MarketMetierDef {
  slug: MarketMetierSlug;
  /** Clé i18n sous marketPages.{market}.metiers.* */
  contentKey: 'educateurCanin' | 'petSitter';
}

export interface MarketDef {
  code: MarketingMarketCode;
  /** Code produit (signup / country_config). */
  productCountry: SupportedCountryCode;
  hubPath: string;
  label: string;
  metiers: MarketMetierDef[];
  /** Langues de contenu exposées sous le hub. */
  contentLangs: MarketContentLang[];
  /**
   * Langue servie au root du hub (`/be` = fr, `/nl` = nl).
   * Les autres langues sont sous `/{market}/{lang}`.
   */
  defaultContentLang: MarketContentLang;
}

export const MARKET_DEFS: Record<MarketingMarketCode, MarketDef> = {
  be: {
    code: 'be',
    productCountry: 'BE',
    hubPath: '/be',
    label: 'Belgique',
    metiers: [
      { slug: 'educateur-canin', contentKey: 'educateurCanin' },
      { slug: 'pet-sitter', contentKey: 'petSitter' },
    ],
    contentLangs: ['fr', 'nl'],
    defaultContentLang: 'fr',
  },
  nl: {
    code: 'nl',
    productCountry: 'NL',
    hubPath: '/nl',
    label: 'Nederland',
    metiers: [
      { slug: 'educateur-canin', contentKey: 'educateurCanin' },
      { slug: 'pet-sitter', contentKey: 'petSitter' },
    ],
    contentLangs: ['nl'],
    defaultContentLang: 'nl',
  },
};

export const MARKET_CODES = Object.keys(MARKET_DEFS) as MarketingMarketCode[];

const MARKET_METIER_SLUGS = new Set<string>(
  MARKET_CODES.flatMap((code) => MARKET_DEFS[code].metiers.map((m) => m.slug))
);

export function getMarketDef(code: MarketingMarketCode): MarketDef {
  return MARKET_DEFS[code];
}

/** Path métier au default lang (`/be/educateur-canin`, `/nl/educateur-canin`). */
export function marketMetierPath(market: MarketingMarketCode, slug: MarketMetierSlug): string {
  return `${MARKET_DEFS[market].hubPath}/${slug}`;
}

/** Hub marché, éventuellement localisé (`/be`, `/be/nl`, `/nl`). */
export function marketHubPath(
  market: MarketingMarketCode,
  lang?: MarketContentLang
): string {
  const def = MARKET_DEFS[market];
  const contentLang = lang ?? def.defaultContentLang;
  const hub = def.hubPath;
  if (contentLang === def.defaultContentLang) return hub;
  return `${hub}/${contentLang}`;
}

/** Métier marché localisé (`/be/...`, `/be/nl/...`, `/nl/...`). */
export function marketLocalizedMetierPath(
  market: MarketingMarketCode,
  slug: MarketMetierSlug,
  lang?: MarketContentLang
): string {
  const def = MARKET_DEFS[market];
  const contentLang = lang ?? def.defaultContentLang;
  if (contentLang === def.defaultContentLang) return marketMetierPath(market, slug);
  return `${def.hubPath}/${contentLang}/${slug}`;
}

/** Toutes les URLs marché indexables (hubs + métiers × langues). */
export function allMarketPaths(): string[] {
  const paths: string[] = [];
  for (const market of MARKET_CODES) {
    const def = MARKET_DEFS[market];
    for (const lang of def.contentLangs) {
      paths.push(marketHubPath(market, lang));
      for (const metier of def.metiers) {
        paths.push(marketLocalizedMetierPath(market, metier.slug, lang));
      }
    }
  }
  return paths;
}

function normalizePathname(pathname: string): string {
  if (!pathname) return '/';
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return pathname.replace(/\/+$/, '') || '/';
  }
  return pathname;
}

export function isMarketPath(pathname: string): boolean {
  return allMarketPaths().includes(normalizePathname(pathname));
}

export function getMarketFromPath(pathname: string): MarketingMarketCode | null {
  const path = normalizePathname(pathname);
  // Hubs plus longs d'abord pour éviter toute ambiguïté future (/be vs /be-…).
  const codes = [...MARKET_CODES].sort(
    (a, b) => MARKET_DEFS[b].hubPath.length - MARKET_DEFS[a].hubPath.length
  );
  for (const code of codes) {
    const hub = MARKET_DEFS[code].hubPath;
    if (path === hub || path.startsWith(`${hub}/`)) return code;
  }
  return null;
}

/** Langue de contenu marché depuis le path (`/be/nl/...` → nl, `/nl/...` → nl, `/be/...` → fr). */
export function getMarketContentLangFromPath(pathname: string): MarketContentLang | null {
  const path = normalizePathname(pathname);
  const market = getMarketFromPath(path);
  if (!market) return null;
  const def = MARKET_DEFS[market];
  const hub = def.hubPath;
  if (path === hub) return def.defaultContentLang;
  const rest = path.slice(hub.length + 1);
  const first = rest.split('/')[0];
  if (
    MARKET_CONTENT_LANGS.includes(first as MarketContentLang) &&
    first !== def.defaultContentLang &&
    def.contentLangs.includes(first as MarketContentLang)
  ) {
    return first as MarketContentLang;
  }
  return def.defaultContentLang;
}

/** Slug métier depuis un path marché (`/be/nl/pet-sitter` → pet-sitter, `/nl/pet-sitter` → pet-sitter). */
export function getMarketMetierSlugFromPath(pathname: string): MarketMetierSlug | null {
  const path = normalizePathname(pathname);
  const market = getMarketFromPath(path);
  if (!market) return null;
  const def = MARKET_DEFS[market];
  const hub = def.hubPath;
  const rest = path.slice(hub.length + 1);
  if (!rest) return null;
  const parts = rest.split('/');
  const lang = getMarketContentLangFromPath(path);
  const slugPart =
    lang && lang !== def.defaultContentLang ? parts[1] : parts[0];
  if (slugPart && MARKET_METIER_SLUGS.has(slugPart)) {
    return slugPart as MarketMetierSlug;
  }
  return null;
}

/** Code i18n pour le copy marché (aligné resources i18next). */
export function marketContentI18nLng(lang: MarketContentLang): 'fr' | 'nl' {
  return lang === 'nl' ? 'nl' : 'fr';
}

export function signupPathForMarket(market: MarketingMarketCode): string {
  const country = MARKET_DEFS[market].productCountry;
  return `/signup-user?country=${country}`;
}

export function signupPathForCountry(country: SupportedCountryCode): string {
  return `/signup-user?country=${country}`;
}

/** Parse `?country=` pour préremplir l’inscription (null si inconnu). */
export function parseSignupCountryParam(raw: string | null | undefined): SupportedCountryCode | null {
  if (!raw) return null;
  const key = raw.trim().toUpperCase().slice(0, 2);
  if (key === 'BE' || key === 'FR' || key === 'NL') return key;
  return null;
}

/** Persistance courte (session) pour enchaîner signup-user → OAuth → signup-professional. */
export const SIGNUP_PREFERRED_COUNTRY_KEY = 'signupPreferredCountry';

export function persistSignupPreferredCountry(country: SupportedCountryCode): void {
  try {
    sessionStorage.setItem(SIGNUP_PREFERRED_COUNTRY_KEY, country);
  } catch {
    /* ignore */
  }
}

export function readSignupPreferredCountry(): SupportedCountryCode | null {
  try {
    return parseSignupCountryParam(sessionStorage.getItem(SIGNUP_PREFERRED_COUNTRY_KEY));
  } catch {
    return null;
  }
}
